import { Loader2, LogOut } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useBrowserWalletStore } from '@/features/browserWallet/store';
import { performAccountLogout } from '@/features/tradingSession/logout';
import { describeErrorText } from '@/lib/errors';
import { shortenAddress } from '@/lib/utils';

import { useFlowStore } from '../store';
import type { TradingFlow } from '../types';

interface ChoiceProps {
    flow: TradingFlow;
    label: string;
    address: string | null;
    onChoose: (flow: TradingFlow) => void;
    disabled: boolean;
}

const Choice: React.FC<ChoiceProps> = ({
    flow,
    label,
    address,
    onChoose,
    disabled,
}) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onChoose(flow)}
        className="flex w-full cursor-pointer flex-col items-start gap-1 rounded-xl border border-border bg-secondary/30 px-4 py-3 text-left transition-colors outline-none hover:border-primary/60 hover:bg-primary/5 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
    >
        <span className="text-sm font-semibold text-foreground">{label}</span>{' '}
        {address && (
            <span className="font-mono text-xs text-muted-foreground">
                {shortenAddress(address)}
            </span>
        )}
    </button>
);

/**
 * Asked once per tab session when both flows are unlocked for the account
 * that just signed in. A choice is required, so neither the overlay nor
 * Escape closes it; Sign out is the way out.
 */
const FlowChoiceDialog: React.FC = () => {
    const open = useFlowStore((state) => state.choiceOpen);
    const availability = useFlowStore((state) => state.availability);
    const choose = useFlowStore((state) => state.choose);
    const heldAddress = useBrowserWalletStore((state) => state.address);
    const [signingOut, setSigningOut] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const v1Address = availability?.v1Address ?? null;
    const v2Address = heldAddress ?? availability?.v2Address ?? null;

    const signOut = async () => {
        setError(null);
        setSigningOut(true);
        try {
            await performAccountLogout();
        } catch (caught) {
            setError(describeErrorText(caught, { operation: 'Sign-out' }));
        } finally {
            setSigningOut(false);
        }
    };

    return (
        <Dialog open={open}>
            <DialogContent
                className="sm:max-w-md"
                showCloseButton={false}
                onEscapeKeyDown={(event) => event.preventDefault()}
                onPointerDownOutside={(event) => event.preventDefault()}
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>Which account do you want to use?</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-2">
                    <Choice
                        flow="v1"
                        label={
                            v1Address
                                ? 'V1 · Custodial wallet'
                                : 'V1 · Custodial wallet, not set up yet'
                        }
                        address={v1Address}
                        onChoose={choose}
                        disabled={signingOut}
                    />
                    <Choice
                        flow="v2"
                        label={
                            heldAddress
                                ? 'V2 · Browser wallet'
                                : 'V2 · Browser wallet, not set up on this browser'
                        }
                        address={v2Address}
                        onChoose={choose}
                        disabled={signingOut}
                    />
                </div>

                <DialogDescription>
                    You can switch any time with the V1/V2 button in the top
                    bar.
                </DialogDescription>

                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={signingOut}
                    onClick={() => void signOut()}
                    className="justify-self-start text-muted-foreground"
                >
                    {signingOut ? (
                        <Loader2 className="size-4 animate-spin" />
                    ) : (
                        <LogOut className="size-4" />
                    )}
                    Sign out
                </Button>

                {error && (
                    <p role="alert" className="text-sm text-destructive">
                        {error}
                    </p>
                )}
            </DialogContent>
        </Dialog>
    );
};

FlowChoiceDialog.displayName = 'FlowChoiceDialog';

export default FlowChoiceDialog;
